import { Fragment } from 'react';
import { Plus, Trash2, Zap } from 'lucide-react';
import type { ConditionExpr, ConditionOperator, ConditionPredicate } from '@/api/client';
import { SUBJECT_STATUSES } from '@/lib/subjectStatus';

export interface RuleField {
  path: string;
  label: string;
  group: 'Subject' | 'Study' | 'This form' | 'Prior forms';
  kind: 'text' | 'number' | 'date' | 'boolean' | 'enum';
  options?: string[];
}

export interface RuleBuilderProps {
  value: ConditionExpr | null;
  onChange: (next: ConditionExpr | null) => void;
  siblingFields?: Array<{
    field_key: string;
    field_label: string;
    field_type?: string;
    options?: Array<{ value: string; label: string }>;
  }>;
  extraFields?: RuleField[];
  compact?: boolean;
}

const SUBJECT_FIELDS: RuleField[] = [
  // Bug found live (UAT): these options used to be lowercase
  // ('female'/'male'/'other'/'unknown') while the real stored value
  // (set at onboarding, see Step3Demographics.tsx's SEX_OPTIONS) is
  // Title Case — a saved "Sex equals male" rule could never match any
  // real subject. Mirror the exact onboarding option strings so a rule
  // built here can actually match.
  { path: 'subject.sex', label: 'Sex', group: 'Subject', kind: 'enum', options: ['Male', 'Female', 'Intersex', 'Unknown', 'Declined to answer'] },
  // Bug found live (UAT): this was free text, so a coordinator typing
  // "men" against a real stored value of "Man" (see
  // Step3Demographics.tsx's GENDER_OPTIONS) could never match. Fixed
  // options close the typo/casing gap at the source, on top of the
  // case-insensitive comparison fix in formConditional.ts.
  { path: 'subject.gender', label: 'Gender', group: 'Subject', kind: 'enum', options: ['Man', 'Woman', 'Non-binary', 'Transgender man', 'Transgender woman', 'Prefer not to say', 'Other'] },
  { path: 'subject.status', label: 'Status', group: 'Subject', kind: 'enum', options: [...SUBJECT_STATUSES] },
  // Bug found live (UAT): ethnicity/race/preferred_language were free
  // text here despite onboarding (Step3Demographics.tsx) only ever
  // offering a fixed picklist — same typo/casing trap as gender. Mirror
  // the exact onboarding option strings.
  { path: 'subject.ethnicity', label: 'Ethnicity', group: 'Subject', kind: 'enum', options: ['Hispanic or Latino', 'Not Hispanic or Latino', 'Unknown', 'Declined to answer'] },
  { path: 'subject.race', label: 'Race', group: 'Subject', kind: 'enum', options: ['American Indian or Alaska Native', 'Asian', 'Black or African American', 'Native Hawaiian or Other Pacific Islander', 'White', 'Multiple', 'Unknown', 'Declined to answer'] },
  {
    path: 'subject.preferred_language',
    label: 'Preferred language',
    group: 'Subject',
    kind: 'enum',
    options: [
      'English', 'Spanish / Español', 'French / Français',
      'Chinese (Simplified) / 中文（简体）', 'Chinese (Traditional) / 中文（繁體）',
      'Vietnamese / Tiếng Việt', 'Korean / 한국어', 'Arabic / العربية',
      'Tagalog / Filipino', 'Portuguese / Português', 'Hindi / हिन्दी',
      'Russian / Русский', 'Japanese / 日本語', 'German / Deutsch',
      'Italian / Italiano', 'Other',
    ],
  },
  { path: 'subject.height_ft', label: 'Height (ft)', group: 'Subject', kind: 'number' },
  { path: 'subject.height_in', label: 'Height (in)', group: 'Subject', kind: 'number' },
  { path: 'subject.weight_lbs', label: 'Weight (lbs)', group: 'Subject', kind: 'number' },
  { path: 'subject.re_consent_required', label: 'Re-consent required', group: 'Subject', kind: 'boolean' },
];

const STUDY_FIELDS: RuleField[] = [
  // Bug found live (UAT): these were Roman numerals ('Phase I'...) but
  // the Study wizard (StudyOnboardingWizardPage.tsx) only ever saves
  // Arabic numerals ('Phase 1'...) — a saved "Phase equals Phase I"
  // rule could never match any real study.
  { path: 'study.phase', label: 'Phase', group: 'Study', kind: 'enum', options: ['Phase 1', 'Phase 2', 'Phase 3', 'Phase 4'] },
  { path: 'study.therapeutic_area', label: 'Therapeutic area', group: 'Study', kind: 'text' },
  { path: 'study.status', label: 'Lifecycle stage', group: 'Study', kind: 'enum', options: ['draft', 'initiation', 'active', 'closed_to_enrollment', 'follow_up', 'completed', 'archived'] },
  { path: 'study.sponsor', label: 'Sponsor', group: 'Study', kind: 'text' },
  { path: 'study.protocol_number', label: 'Protocol number', group: 'Study', kind: 'text' },
  { path: 'study.enrollment_target', label: 'Enrollment target', group: 'Study', kind: 'number' },
  { path: 'study.enrollment_actual', label: 'Enrollment actual', group: 'Study', kind: 'number' },
  { path: 'study.locked', label: 'Study locked', group: 'Study', kind: 'boolean' },
];

const OPS: Array<{ value: ConditionOperator; label: string; needsValue: boolean }> = [
  { value: 'eq', label: 'equals', needsValue: true },
  { value: 'neq', label: 'does not equal', needsValue: true },
  { value: 'gt', label: 'greater than', needsValue: true },
  { value: 'gte', label: 'greater than or equal', needsValue: true },
  { value: 'lt', label: 'less than', needsValue: true },
  { value: 'lte', label: 'less than or equal', needsValue: true },
  { value: 'in', label: 'is one of', needsValue: true },
  { value: 'notin', label: 'is not one of', needsValue: true },
  { value: 'between', label: 'is between', needsValue: true },
  { value: 'exists', label: 'is set', needsValue: false },
  { value: 'empty', label: 'is empty', needsValue: false },
];

function buildInventory(props: RuleBuilderProps): RuleField[] {
  const inv: RuleField[] = [];
  if (props.siblingFields && props.siblingFields.length > 0) {
    for (const f of props.siblingFields) {
      const kind: RuleField['kind'] =
        f.field_type === 'number' || f.field_type === 'calculated' ? 'number'
        : f.field_type === 'date' || f.field_type === 'datetime' ? 'date'
        : f.field_type === 'select' || f.field_type === 'radio' ? 'enum'
        : f.field_type === 'checkbox' ? 'boolean'
        : 'text';
      inv.push({
        path: f.field_key,
        label: f.field_label || f.field_key,
        group: 'This form',
        kind,
        options: (f.options ?? []).map((o) => o.value),
      });
    }
  }
  inv.push(...SUBJECT_FIELDS);
  inv.push(...STUDY_FIELDS);
  if (props.extraFields) inv.push(...props.extraFields);
  return inv;
}

function isCombinator(e: ConditionExpr | null): e is { all: ConditionExpr[] } | { any: ConditionExpr[] } {
  return !!e && ('all' in e || 'any' in e);
}

function isPredicate(e: ConditionExpr | null): e is ConditionPredicate {
  return !!e && 'op' in e && 'field' in e;
}

function blankPredicate(inv: RuleField[]): ConditionExpr {
  const first = inv[0];
  return {
    op: 'eq',
    field: first?.path ?? '',
    value: '',
  };
}

export function RuleBuilder(props: RuleBuilderProps) {
  const { value, onChange } = props;
  const inv = buildInventory(props);

  if (value === null || value === undefined) {
    return (
      <div className="rounded border border-dashed border-surface-border bg-surface-muted/10 p-3 text-center">
        <p className="mb-2 text-[11px] text-ink-muted">
          No conditions yet. Add one to gate visibility.
        </p>
        <button
          type="button"
          onClick={() => onChange(blankPredicate(inv))}
          className="rounded bg-accent-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-700"
        >
          <Plus className="mr-1 inline h-3 w-3" /> Add first condition
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <RuleNode
        node={value}
        onChange={onChange}
        onDelete={() => onChange(null)}
        inventory={inv}
        depth={0}
      />
      <p className="text-[10px] text-ink-muted">
        <Zap className="mr-0.5 inline h-2.5 w-2.5 text-amber-600" />
        Visible only when the conditions above evaluate to true.
      </p>
    </div>
  );
}

function RuleNode({
  node,
  onChange,
  onDelete,
  inventory,
  depth,
}: {
  node: ConditionExpr;
  onChange: (next: ConditionExpr) => void;
  onDelete: () => void;
  inventory: RuleField[];
  depth: number;
}) {
  if (isCombinator(node)) {
    const isAll = 'all' in node;
    const children = isAll ? node.all : node.any;

    function setChildren(next: ConditionExpr[]) {
      if (isAll) onChange({ all: next });
      else onChange({ any: next });
    }

    function flip() {
      if (isAll) onChange({ any: children });
      else onChange({ all: children });
    }

    function addChild() {
      setChildren([...children, blankPredicate(inventory)]);
    }

    function addGroup() {
      setChildren([...children, { all: [blankPredicate(inventory)] }]);
    }

    return (
      <div className={`rounded border border-surface-border ${depth === 0 ? 'bg-surface-muted/10' : 'bg-surface-muted/20'} p-2`}>
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-[11px]">
            <span className="text-ink-muted">Match</span>
            <button
              type="button"
              onClick={flip}
              className="rounded border border-surface-border bg-surface-card px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide hover:bg-surface-muted"
            >
              {isAll ? 'ALL' : 'ANY'}
            </button>
            <span className="text-ink-muted">of these:</span>
          </div>
          <button
            type="button"
            onClick={onDelete}
            className="rounded p-0.5 text-danger-600 hover:bg-danger-50"
            title="Remove group"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>

        <div className="space-y-1.5">
          {children.map((child, i) => (
            <RuleNode
              key={i}
              node={child}
              onChange={(next) => {
                const copy = children.slice();
                copy[i] = next;
                setChildren(copy);
              }}
              onDelete={() => {
                const next = children.filter((_, k) => k !== i);
                if (next.length === 0) onDelete();
                else setChildren(next);
              }}
              inventory={inventory}
              depth={depth + 1}
            />
          ))}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={addChild}
            className="rounded border border-surface-border bg-surface-card px-2 py-0.5 text-[11px] hover:bg-surface-muted"
          >
            <Plus className="mr-0.5 inline h-2.5 w-2.5" /> Add condition
          </button>
          <button
            type="button"
            onClick={addGroup}
            className="rounded border border-surface-border bg-surface-card px-2 py-0.5 text-[11px] hover:bg-surface-muted"
          >
            <Plus className="mr-0.5 inline h-2.5 w-2.5" /> Add group
          </button>
        </div>
      </div>
    );
  }

  if (isPredicate(node)) {
    return (
      <PredicateRow
        node={node}
        onChange={onChange}
        onDelete={onDelete}
        onWrapInGroup={(mode) => {
          if (mode === 'all') onChange({ all: [node, blankPredicate(inventory)] });
          else onChange({ any: [node, blankPredicate(inventory)] });
        }}
        inventory={inventory}
      />
    );
  }

  return null;
}

function PredicateRow({
  node,
  onChange,
  onDelete,
  onWrapInGroup,
  inventory,
}: {
  node: ConditionPredicate;
  onChange: (next: ConditionExpr) => void;
  onDelete: () => void;
  onWrapInGroup: (mode: 'all' | 'any') => void;
  inventory: RuleField[];
}) {
  const opMeta = OPS.find((o) => o.value === node.op) ?? OPS[0]!;
  const fieldMeta = inventory.find((f) => f.path === node.field);

  const grouped: Record<string, RuleField[]> = {};
  for (const f of inventory) {
    (grouped[f.group] = grouped[f.group] ?? []).push(f);
  }

  function setField(next: string) {
    onChange({ ...node, field: next });
  }
  function setOp(next: ConditionOperator) {
    onChange({ ...node, op: next });
  }
  function setValue(next: ConditionPredicate['value']) {
    onChange({ ...node, value: next });
  }

  const fieldInInventory = inventory.some((f) => f.path === node.field);

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded border border-surface-border bg-surface-card p-1.5">
      <select
        value={node.field}
        onChange={(e) => setField(e.target.value)}
        className="min-w-[180px] rounded border border-surface-border bg-surface-card px-1.5 py-1 text-[11px]"
      >
        {(!fieldInInventory || node.field === '') && (
          <option value={node.field} disabled>
            {node.field ? `${node.field} (unknown)` : '— Pick a field —'}
          </option>
        )}
        {Object.entries(grouped).map(([group, items]) => (
          <optgroup key={group} label={group}>
            {items.map((f) => (
              <option key={f.path} value={f.path}>{f.label}</option>
            ))}
          </optgroup>
        ))}
      </select>

      <select
        value={node.op}
        onChange={(e) => setOp(e.target.value as ConditionOperator)}
        className="rounded border border-surface-border px-1.5 py-0.5 text-[11px]"
      >
        {OPS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      {opMeta.needsValue && (
        <ValueInput
          op={node.op}
          fieldMeta={fieldMeta}
          value={node.value}
          onChange={(v) => setValue(v as ConditionPredicate['value'])}
        />
      )}

      <button
        type="button"
        onClick={() => onWrapInGroup('all')}
        className="rounded border border-surface-border bg-surface-card px-1.5 py-0.5 text-[10px] uppercase text-ink-muted hover:bg-surface-muted"
        title="Combine with another condition (ALL must match)"
      >
        + AND
      </button>
      <button
        type="button"
        onClick={() => onWrapInGroup('any')}
        className="rounded border border-surface-border bg-surface-card px-1.5 py-0.5 text-[10px] uppercase text-ink-muted hover:bg-surface-muted"
        title="Combine with another condition (ANY may match)"
      >
        + OR
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="rounded p-0.5 text-danger-600 hover:bg-danger-50"
        title="Remove condition"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

function ValueInput({
  op,
  fieldMeta,
  value,
  onChange,
}: {
  op: string;
  fieldMeta: RuleField | undefined;
  value: unknown;
  onChange: (v: unknown) => void;
}) {
  if (op === 'in' || op === 'notin') {
    const asArr = Array.isArray(value) ? value : [];
    const text = asArr.map(String).join(', ');
    return (
      <input
        type="text"
        value={text}
        onChange={(e) => {
          const raw = e.target.value.split(',').map((s) => s.trim()).filter(Boolean);
          const casted =
            fieldMeta?.kind === 'number'
              ? raw.map((v) => Number(v)).filter((n) => Number.isFinite(n))
              : raw;
          onChange(casted);
        }}
        placeholder="value1, value2"
        className="min-w-[140px] rounded border border-surface-border px-1.5 py-0.5 text-[11px]"
      />
    );
  }

  if (op === 'between') {
    const [lo, hi] = Array.isArray(value) && value.length === 2 ? value : ['', ''];
    return (
      <Fragment>
        <input
          type="number"
          value={lo === null || lo === undefined ? '' : String(lo)}
          onChange={(e) => {
            const next = [
              e.target.value === '' ? '' : Number(e.target.value),
              (Array.isArray(value) ? value[1] : ''),
            ];
            onChange(next);
          }}
          placeholder="min"
          className="w-16 rounded border border-surface-border px-1.5 py-0.5 text-[11px]"
        />
        <span className="text-[11px] text-ink-muted">and</span>
        <input
          type="number"
          value={hi === null || hi === undefined ? '' : String(hi)}
          onChange={(e) => {
            const next = [
              (Array.isArray(value) ? value[0] : ''),
              e.target.value === '' ? '' : Number(e.target.value),
            ];
            onChange(next);
          }}
          placeholder="max"
          className="w-16 rounded border border-surface-border px-1.5 py-0.5 text-[11px]"
        />
      </Fragment>
    );
  }

  if (fieldMeta?.kind === 'boolean') {
    return (
      <select
        value={value === true ? 'true' : value === false ? 'false' : ''}
        onChange={(e) => onChange(e.target.value === 'true')}
        className="rounded border border-surface-border px-1.5 py-0.5 text-[11px]"
      >
        <option value="">—</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  if (fieldMeta?.kind === 'enum' && fieldMeta.options && fieldMeta.options.length > 0) {
    return (
      <select
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-surface-border px-1.5 py-0.5 text-[11px]"
      >
        <option value="">— select —</option>
        {fieldMeta.options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }

  if (fieldMeta?.kind === 'number') {
    return (
      <input
        type="number"
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        placeholder="value"
        className="w-24 rounded border border-surface-border px-1.5 py-0.5 text-[11px]"
      />
    );
  }

  if (fieldMeta?.kind === 'date') {
    return (
      <input
        type="date"
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
        className="rounded border border-surface-border px-1.5 py-0.5 text-[11px]"
      />
    );
  }

  return (
    <input
      type="text"
      value={value === undefined || value === null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      placeholder="value"
      className="min-w-[100px] rounded border border-surface-border px-1.5 py-0.5 text-[11px]"
    />
  );
}
